import React, { useState, useEffect } from 'react';
import { 
  FiRefreshCw, 
  FiAlertCircle,
  FiCheckCircle,
  FiActivity,
  FiUser,
  FiPackage,
  FiLock,
  FiMonitor,
  FiPlus,
  FiX,
  FiTrash2,
  FiClock,
  FiSearch
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import LoginActivityApi from '../../baseUrl/LoginActivityApi';
import './AppLoginActivityCards.css';

interface AppLoginStatus {
  id: number;
  app_name: string;
  app_status_code: string | null;
  app_status_message: string | null;
  created_at: string;
  updated_at: string | null;
}

// Toolbar button styles
const toolbarButtonBase: React.CSSProperties = {
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s ease',
  position: 'relative'
};

const AppLoginActivityCards: React.FC = () => {
  const navigate = useNavigate();
  const [loginStatuses, setLoginStatuses] = useState<AppLoginStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [selectedApp, setSelectedApp] = useState<AppLoginStatus | null>(null);
  
  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginSuccess, setLoginSuccess] = useState(false);
  
  const [showLoginForm, setShowLoginForm] = useState(false);

  // Create app login state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAppName, setNewAppName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState(false);

  // Delete state
  const [deleteLoading, setDeleteLoading] = useState<number | null>(null);

  // Check status state
  const [checkingStatus, setCheckingStatus] = useState<number | null>(null);

  const fetchLoginStatuses = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await LoginActivityApi.getAllLoginStatuses();
      
      // Handle the nested response structure
      let data: AppLoginStatus[] = [];
      
      // Check if response is an array with a data property
      if (Array.isArray(response) && response.length > 0 && response[0].data) {
        data = response[0].data;
      } 
      // Check if response has a data property directly
      else if (response && response.data) {
        data = response.data;
      }
      // Handle if response is already the array of statuses
      else if (Array.isArray(response)) {
        data = response;
      }
      // Handle single object response
      else if (response && typeof response === 'object') {
        data = [response];
      }
      
      setLoginStatuses(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch login statuses');
      console.error('Error fetching login statuses:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchLoginStatuses();
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchLoginStatuses();
    }, 30 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Check if status code is success (200 or 201 only)
  // app_status_code contains the numeric code (e.g., "200", "500")
  const isSuccessStatus = (statusCode: string | null): boolean => {
    if (!statusCode) return false;
    return statusCode === '200' || statusCode === '201';
  };

  // Get color based on app_status_code
  // Green for 200/201, Red for all others
  const getStatusColor = (statusCode: string | null) => {
    if (!statusCode) return '#9ca3af'; // gray for null/pending
    return isSuccessStatus(statusCode) ? '#10b981' : '#ef4444';
  };

  // Check if has error (not 200 or 201)
  const hasError = (statusCode: string | null) => {
    if (!statusCode) return false;
    return !isSuccessStatus(statusCode);
  };

  const isPending = (statusCode: string | null, statusMessage: string | null) => {
    return !statusCode && !statusMessage;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleOpenLoginForm = () => {
    if (selectedApp) {
      setShowLoginForm(true);
      // Reset form when opening modal
      setEmail('');
      setPassword('');
      setLoginError(null);
      setLoginSuccess(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedApp(null);
    setEmail('');
    setPassword('');
    setSubmitting(false);
    setLoginError(null);
    setLoginSuccess(false);
    setShowLoginForm(false);
  };

  const handleSubmitLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setLoginError('Please enter both email and password');
      return;
    }

    setSubmitting(true);
    setLoginError(null);
    setLoginSuccess(false);

    try {
      if (!selectedApp) return;

      const response = await LoginActivityApi.loginToApp(selectedApp.app_name, {
        email,
        password
      });

      // Check if login was successful
      if (response.success && response.data.success) {
        setLoginSuccess(true);
        setLoginError(null);
        
        // Show success message
        setTimeout(() => {
          handleCloseModal();
          // Refresh statuses after successful login
          fetchLoginStatuses();
        }, 2000);
      } else {
        // Login failed
        setLoginError(response.data.message || response.message || 'Login failed');
        setLoginSuccess(false);
      }
    } catch (err: any) {
      console.error('Login error:', err);
      
      // Handle error response
      if (err.response?.data) {
        const errorData = err.response.data;
        setLoginError(errorData.data?.message || errorData.message || 'Login failed');
      } else {
        setLoginError(err.message || 'Failed to submit login credentials');
      }
      setLoginSuccess(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateAppLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newAppName.trim()) {
      setCreateError('Please enter an app name');
      return;
    }

    setCreateLoading(true);
    setCreateError(null);
    setCreateSuccess(false);

    try {
      const response = await LoginActivityApi.createAppLogin(newAppName.trim());

      if (response.success) {
        setCreateSuccess(true);
        setCreateError(null);
        
        // Show success message and close modal after delay
        setTimeout(() => {
          setShowCreateModal(false);
          setNewAppName('');
          setCreateSuccess(false);
          // Refresh the list
          fetchLoginStatuses();
        }, 2000);
      }
    } catch (err: any) {
      console.error('Create error:', err);
      
      if (err.status === 422) {
        setCreateError(err.message || 'App name already exists');
      } else {
        setCreateError(err.message || 'Failed to create app login');
      }
      setCreateSuccess(false);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteAppLogin = async (app: AppLoginStatus) => {
    if (!window.confirm(`Are you sure you want to delete "${app.app_name}"?`)) {
      return;
    }

    setDeleteLoading(app.id);

    try {
      const response = await LoginActivityApi.deleteAppLogin(app.id);
      
      if (response.success) {
        // Remove from local state
        setLoginStatuses(prev => prev.filter(item => item.id !== app.id));
        
        // Show success message (optional)
        console.log(`Successfully deleted ${app.app_name}`);
      }
    } catch (err: any) {
      console.error('Delete error:', err);
      alert(`Failed to delete app login: ${err.message || 'Unknown error'}`);
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleRemoteAccess = (app: AppLoginStatus) => {
    // Navigate to remote server page
    navigate('/remote-server');
  };

  // Handle check status button click
  const handleCheckStatus = async (app: AppLoginStatus) => {
    setCheckingStatus(app.id);
    
    try {
      const response = await LoginActivityApi.checkLoginStatus(app.app_name);
      
      if (response.success) {
        // Show success notification
        console.log(`Status check completed for ${app.app_name}:`, response.data);
        
        // Refresh the list to get updated status
        await fetchLoginStatuses();
      }
    } catch (err: any) {
      console.error('Check status error:', err);
      alert(`Failed to check status for ${app.app_name}: ${err.message || 'Unknown error'}`);
    } finally {
      setCheckingStatus(null);
    }
  };

  // Toolbar button component with tooltip
  const ToolbarButton: React.FC<{
    onClick: () => void;
    disabled?: boolean;
    gradient: string;
    icon: React.ReactNode;
    label: string;
  }> = ({ onClick, disabled, gradient, icon, label }) => (
    <button
      className="toolbar-btn"
      onClick={onClick}
      disabled={disabled}
      data-tooltip={label}
      style={{
        ...toolbarButtonBase,
        background: gradient,
        color: 'white',
        opacity: disabled ? 0.7 : 1
      }}
    >
      {icon}
    </button>
  );

  if (loading && loginStatuses.length === 0) {
    return (
      <div className="loading-container">
        <FiRefreshCw className="loading-spinner" />
        <p>Loading app login statuses...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <FiAlertCircle className="error-icon" />
        <p>Error: {error}</p>
        <button onClick={fetchLoginStatuses} className="retry-btn">
          <FiRefreshCw /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="automation-properties-container">
      <div className="properties-header">
        <div>
          <h1>App Login Activity</h1>
          <p className="page-subtitle">
            Real-time monitoring of application login statuses
          </p>
        </div>
        <div className="header-actions">
          {lastUpdated && (
            <span className="last-updated">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button 
            onClick={() => setShowCreateModal(true)} 
            className="create-btn"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <FiPlus />
            Create App Login
          </button>
          <button 
            onClick={fetchLoginStatuses} 
            className="refresh-btn"
            disabled={loading}
          >
            <FiRefreshCw className={loading ? 'spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      <div className="properties-grid">
        {loginStatuses.length === 0 ? (
          <div className="no-properties">
            <FiPackage className="no-properties-icon" />
            <h3>No Login Activity</h3>
            <p>No application login statuses available at the moment</p>
            <button 
              onClick={() => setShowCreateModal(true)} 
              className="create-btn"
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                marginTop: '16px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              <FiPlus /> Create First App Login
            </button>
          </div>
        ) : (
          loginStatuses.map((app) => {
            // app_status_code contains the numeric code (200, 500, etc.)
            // app_status_message contains the message text
            const isError = hasError(app.app_status_code);
            const isPendingStatus = isPending(app.app_status_code, app.app_status_message);
            const statusColor = getStatusColor(app.app_status_code);
            
            return (
              <div 
                key={app.id} 
                className={`property-card ${isError ? 'clickable-card' : ''}`}
                style={{ cursor: 'default', position: 'relative' }}
              >
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="property-id-badge">
                    <FiActivity />
                    <span>{app.app_name}</span>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div 
                      className={`status-badge ${isPendingStatus ? 'pending' : isError ? 'error' : 'success'}`} 
                      style={{ color: statusColor }}
                    >
                      {isPendingStatus ? (
                        <>
                          <FiClock />
                          <span>Pending</span>
                        </>
                      ) : isError ? (
                        <>
                          <FiAlertCircle />
                          <span>Error</span>
                        </>
                      ) : (
                        <>
                          <FiCheckCircle />
                          <span>OK</span>
                        </>
                      )}
                    </div>
                    
                    {/* Delete button in header with border */}
                    <button
                      className="delete-header-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteAppLogin(app);
                      }}
                      disabled={deleteLoading === app.id}
                      style={{
                        background: 'rgba(239, 68, 68, 0.05)',
                        color: '#ef4444',
                        border: '1px solid #ef4444',
                        borderRadius: '6px',
                        padding: '6px 8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        minWidth: '32px',
                        height: '32px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
                        e.currentTarget.style.borderColor = '#dc2626';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.05)';
                        e.currentTarget.style.borderColor = '#ef4444';
                      }}
                      title={`Delete ${app.app_name}`}
                    >
                      {deleteLoading === app.id ? (
                        <FiRefreshCw className="spin" size={16} />
                      ) : (
                        <FiX size={16} strokeWidth={2.5} />
                      )}
                    </button>
                  </div>
                </div>

                <div className="card-body">
                  {/* STATUS CODE - Shows app_status_code (numeric code like 200, 500) */}
                  <div className="group-id-section">
                    <label>Status Code</label>
                    <code style={{ 
                      color: statusColor,
                      fontWeight: 600
                    }}>
                      {app.app_status_code || 'N/A'}
                    </code>
                  </div>

                  {/* STATUS MESSAGE - Shows app_status_message (the descriptive message) */}
                  <div className="applications-section">
                    <label>Status Message</label>
                    <div className="app-badges">
                      <div 
                        className={`app-badge ${isPendingStatus ? 'pending-status' : isError ? 'failed-status' : 'completed-status'}`}
                        style={{ cursor: 'default' }}
                      >
                        <div 
                          className="status-indicator" 
                          style={{ backgroundColor: statusColor }}
                        ></div>
                        <span className="app-name" style={{
                          color: 'var(--text-primary)',
                          fontSize: '14px'
                        }}>
                          {app.app_status_message || 'No status message yet'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div style={{ 
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '16px',
                    marginTop: '12px'
                  }}>
                    <div className="group-id-section" style={{ marginBottom: 0 }}>
                      <label>Created</label>
                      <code style={{ 
                        fontSize: '11px',
                        padding: '6px 10px'
                      }}>
                        {formatDate(app.created_at)}
                      </code>
                    </div>
                    <div className="group-id-section" style={{ marginBottom: 0 }}>
                      <label>Updated</label>
                      <code style={{ 
                        fontSize: '11px',
                        padding: '6px 10px'
                      }}>
                        {formatDate(app.updated_at)}
                      </code>
                    </div>
                  </div>

                  {/* Card Footer with Toolbar Buttons */}
                  <div className="card-footer" style={{ marginTop: '16px' }}>
                    <div className="status-summary">
                      {isError && (
                        <div className="summary-item" style={{ color: '#ef4444' }}>
                          <FiAlertCircle />
                          <span>Has error</span>
                        </div>
                      )}
                      {isPendingStatus && (
                        <div className="summary-item" style={{ color: '#9ca3af' }}>
                          <FiClock />
                          <span>Awaiting first login</span>
                        </div>
                      )}
                      {!isError && !isPendingStatus && (
                        <div className="summary-item" style={{ color: '#10b981' }}>
                          <FiCheckCircle />
                          <span>Status OK</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Toolbar with icon-only buttons */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '8px',
                      flexWrap: 'nowrap'
                    }}>
                      {/* Check Status Button */}
                      <ToolbarButton
                        onClick={() => handleCheckStatus(app)}
                        disabled={checkingStatus === app.id}
                        gradient="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
                        icon={checkingStatus === app.id ? <FiRefreshCw className="spin" size={20} /> : <FiSearch size={20} />}
                        label="Check Status"
                      />
                      
                      {/* Remote Access Button */}
                      <ToolbarButton
                        onClick={() => handleRemoteAccess(app)}
                        gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                        icon={<FiMonitor size={20} />}
                        label="Remote Access"
                      />
                      
                      {/* Manual Login Button */}
                      <ToolbarButton
                        onClick={() => {
                          setSelectedApp(app);
                          setTimeout(() => handleOpenLoginForm(), 0);
                        }}
                        gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                        icon={<FiUser size={20} />}
                        label="Manual Login"
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Create App Login Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FiPlus style={{ color: '#667eea' }} /> Create App Login
              </h2>
              <button 
                className="modal-close"
                onClick={() => setShowCreateModal(false)}
                disabled={createLoading}
              >
                ×
              </button>
            </div>
            
            <div className="modal-content modal-content-no-style">
              <form onSubmit={handleCreateAppLogin}>
                <div className="update-form">
                  {/* Success Message */}
                  {createSuccess && (
                    <div className="update-form-item" style={{
                      background: 'rgba(16, 185, 129, 0.1)',
                      borderLeft: '3px solid #10b981'
                    }}>
                      <div className="update-property-info">
                        <strong style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FiCheckCircle /> App Login Created Successfully!
                        </strong>
                        <small style={{ color: '#10b981', fontSize: '14px', marginTop: '4px' }}>
                          App name "{newAppName}" has been created.
                        </small>
                      </div>
                    </div>
                  )}

                  {/* Error Message */}
                  {createError && !createSuccess && (
                    <div className="update-form-item" style={{
                      background: 'rgba(239, 68, 68, 0.1)',
                      borderLeft: '3px solid #ef4444'
                    }}>
                      <div className="update-property-info">
                        <strong style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FiAlertCircle /> Creation Failed
                        </strong>
                        <small style={{ color: '#ef4444', fontSize: '14px', marginTop: '4px' }}>
                          {createError}
                        </small>
                      </div>
                    </div>
                  )}

                  {/* App Name Field */}
                  <div className="form-group">
                    <label>App Name</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={newAppName}
                        onChange={(e) => setNewAppName(e.target.value)}
                        placeholder="Enter app name"
                        required
                        disabled={createLoading || createSuccess}
                        style={{
                          width: '100%',
                          padding: '10px 12px 10px 40px',
                          fontSize: '14px'
                        }}
                      />
                      <FiPackage style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--text-secondary)'
                      }} />
                    </div>
                  </div>

                  {/* Info Message */}
                  {!createSuccess && (
                    <div style={{
                      padding: '12px',
                      background: 'rgba(102, 126, 234, 0.1)',
                      border: '1px solid rgba(102, 126, 234, 0.3)',
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: 'var(--text-secondary)',
                      lineHeight: '1.5'
                    }}>
                      <strong style={{ color: 'var(--text-primary)' }}>Note:</strong> Enter a unique app name for the new login activity. This will create a new monitoring entry for the specified application.
                    </div>
                  )}
                </div>
              </form>
            </div>
            
            <div className="modal-footer">
              <button 
                type="button"
                className="btn-secondary"
                onClick={() => setShowCreateModal(false)}
                disabled={createLoading}
              >
                {createSuccess ? 'Close' : 'Cancel'}
              </button>
              {!createSuccess && (
                <button 
                  type="button"
                  className="btn-primary"
                  onClick={handleCreateAppLogin}
                  disabled={createLoading || !newAppName.trim()}
                >
                  {createLoading ? (
                    <>
                      <FiRefreshCw className="spin" /> Creating...
                    </>
                  ) : (
                    <>
                      <FiCheckCircle /> Create App Login
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Login Account Modal */}
      {selectedApp && showLoginForm && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FiUser style={{ color: '#667eea' }} /> Manual Login - {selectedApp.app_name}
              </h2>
              <button 
                className="modal-close"
                onClick={handleCloseModal}
                disabled={submitting}
              >
                ×
              </button>
            </div>
            
            <div className="modal-content modal-content-no-style">
              <form onSubmit={handleSubmitLogin}>
                <div className="update-form">
                  {/* Status Info */}
                  {(() => {
                    const isErrorStatus = hasError(selectedApp.app_status_code);
                    const isPendingStatus = isPending(selectedApp.app_status_code, selectedApp.app_status_message);
                    const statusColor = getStatusColor(selectedApp.app_status_code);
                    
                    return (
                      <div className="update-form-item" style={{
                        background: isPendingStatus 
                          ? 'rgba(156, 163, 175, 0.1)' 
                          : isErrorStatus 
                            ? 'rgba(239, 68, 68, 0.1)' 
                            : 'rgba(16, 185, 129, 0.1)',
                        borderLeft: isPendingStatus 
                          ? '3px solid #9ca3af' 
                          : isErrorStatus 
                            ? '3px solid #ef4444' 
                            : '3px solid #10b981'
                      }}>
                        <div className="update-property-info">
                          <strong style={{ 
                            color: isPendingStatus 
                              ? '#9ca3af' 
                              : isErrorStatus 
                                ? '#ef4444' 
                                : '#10b981' 
                          }}>
                            Current Status
                          </strong>
                          <small style={{ 
                            color: isPendingStatus 
                              ? '#9ca3af' 
                              : isErrorStatus 
                                ? '#ef4444' 
                                : '#10b981', 
                            fontSize: '14px', 
                            marginTop: '4px' 
                          }}>
                            {isPendingStatus 
                              ? 'No login attempt yet'
                              : `Status ${selectedApp.app_status_code}: ${selectedApp.app_status_message}`
                            }
                          </small>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Success Message */}
                  {loginSuccess && (
                    <div className="update-form-item" style={{
                      background: 'rgba(16, 185, 129, 0.1)',
                      borderLeft: '3px solid #10b981'
                    }}>
                      <div className="update-property-info">
                        <strong style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FiCheckCircle /> Login Successful!
                        </strong>
                        <small style={{ color: '#10b981', fontSize: '14px', marginTop: '4px' }}>
                          Account authenticated successfully. Refreshing status...
                        </small>
                      </div>
                    </div>
                  )}

                  {/* Error Message */}
                  {loginError && !loginSuccess && (
                    <div className="update-form-item" style={{
                      background: 'rgba(239, 68, 68, 0.1)',
                      borderLeft: '3px solid #ef4444'
                    }}>
                      <div className="update-property-info">
                        <strong style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FiAlertCircle /> Login Failed
                        </strong>
                        <small style={{ color: '#ef4444', fontSize: '14px', marginTop: '4px' }}>
                          {loginError}
                        </small>
                      </div>
                    </div>
                  )}

                  {/* Email Field */}
                  <div className="form-group">
                    <label>Email Address</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter email address"
                        required
                        disabled={submitting || loginSuccess}
                        style={{
                          width: '100%',
                          padding: '10px 12px 10px 40px',
                          fontSize: '14px'
                        }}
                      />
                      <FiUser style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--text-secondary)'
                      }} />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div className="form-group">
                    <label>Password</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter password"
                        required
                        disabled={submitting || loginSuccess}
                        style={{
                          width: '100%',
                          padding: '10px 12px 10px 40px',
                          fontSize: '14px'
                        }}
                      />
                      <FiLock style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--text-secondary)'
                      }} />
                    </div>
                  </div>

                  {/* Info Message */}
                  {!loginSuccess && (
                    <div style={{
                      padding: '12px',
                      background: 'rgba(102, 126, 234, 0.1)',
                      border: '1px solid rgba(102, 126, 234, 0.3)',
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: 'var(--text-secondary)',
                      lineHeight: '1.5'
                    }}>
                      <strong style={{ color: 'var(--text-primary)' }}>Note:</strong> These credentials will be used to re-authenticate the {selectedApp.app_name} automation account.
                    </div>
                  )}
                </div>
              </form>
            </div>
            
            <div className="modal-footer">
              <button 
                type="button"
                className="btn-secondary"
                onClick={handleCloseModal}
                disabled={submitting}
              >
                {loginSuccess ? 'Close' : 'Cancel'}
              </button>
              {!loginSuccess && (
                <button 
                  type="button"
                  className="btn-primary"
                  onClick={handleSubmitLogin}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <FiRefreshCw className="spin" /> Submitting...
                    </>
                  ) : (
                    <>
                      <FiCheckCircle /> Submit Login
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppLoginActivityCards;
